/**
 * Client-side, WhatsApp-style video compression using ffmpeg.wasm.
 *
 * Why client-side? It re-encodes the video in the browser BEFORE upload so
 * (a) we never push a huge raw file through the K8s ingress body limit, and
 * (b) it works identically in preview and production with zero server infra
 * (no ffmpeg binary needed on the backend). The single-threaded core does
 * NOT require SharedArrayBuffer, so no COOP/COEP headers are needed.
 *
 * Rules (per product spec):
 *   - Max duration 20s — longer videos are rejected up front.
 *   - Re-encoded H.264 / AAC, longest side capped to 960px, 30fps, faststart.
 *   - Hard 30MB ceiling; a second harsher pass kicks in if the first is over.
 *
 * NOTE: the @ffmpeg/* packages are NOT statically imported here. Webpack
 * rewrites the FFmpeg class's internal `new Worker(new URL(...))` through its
 * own resolver, which mangled our self-hosted worker URL and broke loading in
 * the bundled app. We instead load the class via a native `webpackIgnore`
 * dynamic import from /public/ffmpeg/esm and inline the two tiny util helpers.
 */

export const MAX_VIDEO_DURATION_S = 20;
export const MAX_VIDEO_BYTES = 30 * 1024 * 1024;

// Inlined equivalents of @ffmpeg/util's toBlobURL / fetchFile so we don't pull
// the package (and its worker-creating sibling) into the webpack bundle.
async function toBlobURL(url, mimeType) {
  const buf = await (await fetch(url)).arrayBuffer();
  return URL.createObjectURL(new Blob([buf], { type: mimeType }));
}
async function fetchFile(file) {
  return new Uint8Array(await file.arrayBuffer());
}

const VIDEO_EXT_RE = /\.(mp4|mov|webm|m4v|avi|mkv|3gp|ogv)$/i;

export function isVideoFile(file) {
  return !!file && ((file.type || "").startsWith("video/") || VIDEO_EXT_RE.test(file.name || ""));
}

export function isVideoPath(path) {
  return !!path && VIDEO_EXT_RE.test(path);
}

/** Read duration (seconds) from a video file via a throwaway <video> element. */
export function getVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(v.duration || 0);
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read this video file."));
    };
    v.src = url;
  });
}

let _ffmpeg = null;
let _loadPromise = null;

async function getFFmpeg() {
  if (_ffmpeg) return _ffmpeg;
  if (!_loadPromise) {
    _loadPromise = (async () => {
      const BASE = `${window.location.origin}/ffmpeg`;
      // Load the FFmpeg class via a NATIVE dynamic import (webpackIgnore) so
      // webpack never rewrites its internal `new Worker(new URL(...))` through
      // its own resolver — that rewrite mangled our self-hosted classWorkerURL
      // and caused "Video compressor failed to start" in the bundled app
      // (it worked only when the class ran natively). All assets are
      // self-hosted under /public/ffmpeg (ESM worker + ESM single-thread core),
      // so there is no third-party CDN dependency at runtime.
      //
      // NOTE: the multi-thread core (@ffmpeg/core-mt) is intentionally NOT used
      // here. Its emscripten pthread bootstrap is incompatible with our
      // self-hosted *module-type* class worker (pthread workers hang at 0%),
      // and switching the class worker back to the bundled one re-triggers the
      // webpack worker-URL mangling bug. Speed instead comes from the
      // `-preset ultrafast` encode below, which works in every browser.
      const mod = await import(/* webpackIgnore: true */ `${BASE}/esm/index.js`);
      const ff = new mod.FFmpeg();
      await ff.load({
        classWorkerURL: `${BASE}/ffmpeg-worker.js`,
        coreURL: await toBlobURL(`${BASE}/ffmpeg-core.js`, "text/javascript"),
        wasmURL: await toBlobURL(`${BASE}/ffmpeg-core.wasm`, "application/wasm"),
      });
      // eslint-disable-next-line no-console
      console.info("ffmpeg.wasm loaded");
      _ffmpeg = ff;
      return ff;
    })();
  }
  try {
    return await _loadPromise;
  } catch (e) {
    _loadPromise = null; // allow a retry on the next attempt
    // Preserve the underlying cause for debugging — the user-facing message
    // below is intentionally generic.
    // eslint-disable-next-line no-console
    console.warn("ffmpeg.load failed:", e);
    throw new Error("Video processor failed to start. Please reload the page and try again.");
  }
}

function buildArgs(input, output, { scale, crf }) {
  return [
    "-i", input,
    "-vf", `scale='min(${scale},iw)':'min(${scale},ih)':force_original_aspect_ratio=decrease:force_divisible_by=2,fps=30`,
    // ultrafast preset prioritises encode speed — the single-thread wasm core
    // is the bottleneck, so this is the biggest safe lever we have. Slightly
    // larger output is caught by the 30MB ceiling + second pass below.
    "-c:v", "libx264", "-preset", "ultrafast", "-crf", String(crf), "-pix_fmt", "yuv420p",
    "-c:a", "aac", "-b:a", "128k",
    "-movflags", "+faststart",
    output,
  ];
}

/**
 * Compress a video file to a WhatsApp-style MP4 (<=30MB, <=20s).
 * @param {File} file
 * @param {(pct:number)=>void} onProgress 0..100, -1 while loading tools
 * @returns {Promise<File>} a new .mp4 File ready for upload
 */
export async function compressVideo(file, onProgress = () => {}) {
  // Read duration to enforce the 20s cap. If the browser can't read the
  // metadata (some quirky/synthetic mp4s), don't block the upload — fall
  // through and let the 30MB ceiling be the backstop.
  let duration = 0;
  try {
    duration = await getVideoDuration(file);
  } catch {
    duration = 0;
  }
  if (duration > MAX_VIDEO_DURATION_S + 0.9) {
    throw new Error(`Video is ${Math.round(duration)}s — the maximum is ${MAX_VIDEO_DURATION_S} seconds.`);
  }

  onProgress(-1); // "loading tools" state
  const ff = await getFFmpeg();

  const inName = "in" + (file.name.match(VIDEO_EXT_RE)?.[0] || ".mp4");
  const outName = "out.mp4";

  const progressHandler = ({ progress }) => {
    if (typeof progress === "number" && progress >= 0 && progress <= 1) {
      onProgress(Math.min(99, Math.round(progress * 100)));
    }
  };
  ff.on("progress", progressHandler);

  try {
    await ff.writeFile(inName, await fetchFile(file));

    // Pass 1 — balanced quality.
    onProgress(0);
    await ff.exec(buildArgs(inName, outName, { scale: 960, crf: 28 }));
    let data = await ff.readFile(outName);

    // Pass 2 — if still over the ceiling, re-encode harder/smaller.
    if (data.byteLength > MAX_VIDEO_BYTES) {
      await ff.deleteFile(outName).catch(() => {});
      onProgress(0);
      await ff.exec(buildArgs(inName, outName, { scale: 640, crf: 32 }));
      data = await ff.readFile(outName);
    }

    if (data.byteLength > MAX_VIDEO_BYTES) {
      throw new Error("Video is still over 30MB after compression. Please trim it shorter.");
    }
    if (!data || data.byteLength === 0) {
      // ffmpeg ran but produced nothing — the input wasn't a decodable video.
      throw new Error("Couldn't read that video — please choose a valid video file.");
    }

    onProgress(100);
    const base = (file.name.replace(VIDEO_EXT_RE, "") || "video").slice(0, 60);
    const video = new File([data], `${base}.mp4`, { type: "video/mp4" });

    // Grab a poster frame (~0.1s in) so gallery/bucket tiles show a real
    // thumbnail instead of a generic play badge. Best-effort — never blocks.
    let poster = null;
    try {
      await ff.exec([
        "-ss", "0.1", "-i", outName, "-frames:v", "1",
        "-vf", "scale='min(640,iw)':-2", "-q:v", "5", "poster.jpg",
      ]);
      const pdata = await ff.readFile("poster.jpg");
      if (pdata && pdata.byteLength > 0) poster = new Blob([pdata], { type: "image/jpeg" });
    } catch {
      poster = null;
    }

    return { video, poster };
  } finally {
    ff.off("progress", progressHandler);
    await ff.deleteFile(inName).catch(() => {});
    await ff.deleteFile(outName).catch(() => {});
    await ff.deleteFile("poster.jpg").catch(() => {});
  }
}

/** Derived storage key for a video's poster thumbnail (see backend upload). */
export function posterKey(videoPath) {
  return videoPath ? `${videoPath}.poster.jpg` : null;
}
